import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { aiApi } from '../../utils/aiApi';

export interface AIKnowledgeItem {
  id: string;
  title: string;
  content: string;
  type: 'document' | 'faq' | 'url';
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface AIKnowledgeState {
  items: AIKnowledgeItem[];
  loading: boolean;
  error: string | null;
  searchResults: AIKnowledgeItem[];
}

const initialState: AIKnowledgeState = {
  items: [],
  loading: false,
  error: null,
  searchResults: [],
};

export const fetchAIKnowledgeItems = createAsyncThunk(
  'aiKnowledge/fetchAll',
  async () => {
    const response = await aiApi.get('/knowledge');
    return response.data;
  }
);

export const createAIKnowledgeItem = createAsyncThunk(
  'aiKnowledge/create',
  async (itemData: Partial<AIKnowledgeItem>) => {
    const response = await aiApi.post('/knowledge', itemData);
    return response.data;
  }
);

export const searchAIKnowledge = createAsyncThunk(
  'aiKnowledge/search',
  async (query: string) => {
    const response = await aiApi.get(`/knowledge/search?q=${encodeURIComponent(query)}`);
    return response.data;
  }
);

const aiKnowledgeSlice = createSlice({
  name: 'aiKnowledge',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAIKnowledgeItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIKnowledgeItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAIKnowledgeItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch knowledge items';
      })
      .addCase(createAIKnowledgeItem.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(searchAIKnowledge.fulfilled, (state, action) => {
        state.searchResults = action.payload;
      });
  },
});

export const { clearSearchResults, clearError } = aiKnowledgeSlice.actions;
export default aiKnowledgeSlice.reducer;